
import { ValidationError } from './validation';
import { ERROR_MESSAGES } from './constants';


export const ErrorType = {
  VALIDATION: 'VALIDATION',
  AUTHENTICATION: 'AUTHENTICATION',
  AUTHORIZATION: 'AUTHORIZATION',
  NOT_FOUND: 'NOT_FOUND',
  CONFLICT: 'CONFLICT',
  RATE_LIMIT: 'RATE_LIMIT',
  DATABASE: 'DATABASE',
  NETWORK: 'NETWORK',
  UNKNOWN: 'UNKNOWN',
} as const;

export type ErrorType = typeof ErrorType[keyof typeof ErrorType];


export class AppError extends Error {
  type: ErrorType;
  userMessage?: string;
  code?: string;
  statusCode?: number;

  constructor(
    message: string,
    type: ErrorType = ErrorType.UNKNOWN,
    userMessage?: string,
    code?: string,
    statusCode?: number
  ) {
    super(message);
    this.name = 'AppError';
    this.type = type;
    this.userMessage = userMessage;
    this.code = code;
    this.statusCode = statusCode;
  }
}


const USER_MESSAGES: Record<string, string> = {
  // Autenticação
  'Invalid login credentials': ERROR_MESSAGES.INVALID_CREDENTIALS,
  'Email not confirmed': ERROR_MESSAGES.EMAIL_NOT_CONFIRMED,
  'User not found': 'Usuário não encontrado. Verifique o e-mail informado.',
  'User already registered': 'Este e-mail já está cadastrado. Tente fazer login ou use outro e-mail.',
  'too many requests': ERROR_MESSAGES.RATE_LIMIT,

  // Autorização
  'Unauthorized': ERROR_MESSAGES.PERMISSION_DENIED,
  'Forbidden': ERROR_MESSAGES.PERMISSION_DENIED,

  // Rede
  'Network error': ERROR_MESSAGES.NETWORK_ERROR,
  'Failed to fetch': ERROR_MESSAGES.NETWORK_ERROR,

  // Recursos
  'Not found': ERROR_MESSAGES.RESOURCE_NOT_FOUND,
  'Conflict': ERROR_MESSAGES.DUPLICATE_RESOURCE,
};


export function getUserMessage(error: unknown): string {

  if (error instanceof ValidationError) {
    return error.message;
  }


  if (error instanceof AppError && error.userMessage) {
    return error.userMessage;
  }


  if (error instanceof Error) {

    const specificErrors = [
      'ID da ata inválido',
      'ID do tópico inválido',
      'ID do projeto inválido',
      'ID da ata inválido',
      'Resposta inválida do servidor',
      'Resposta inválida do servidor ao criar tópico',
      'Resposta inválida do servidor ao atualizar tópico',
      'Resposta inválida do servidor ao criar ata',
      'Resposta inválida do servidor ao atualizar ata',
      'Data inválida',
      'Localização não pode conter mais de 200 caracteres',
      'Número da ata deve ser um número positivo',
      'Data da reunião é obrigatória',
    ];
    
    for (const specificError of specificErrors) {
      if (error.message.includes(specificError)) {
        return error.message;
      }
    }


    for (const [key, value] of Object.entries(USER_MESSAGES)) {
      if (error.message.includes(key)) {
        return value;
      }
    }


    if (error.message.includes('timeout') || error.message.includes('ECONNABORTED') || error.message.includes('Tempo limite excedido')) {
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    }


    if (error.message.includes('Invalid')) {
      return 'Dados inválidos. Verifique os campos preenchidos e tente novamente.';
    }

    if (error.message.includes('duplicate key')) {
      return ERROR_MESSAGES.DUPLICATE_RESOURCE;
    }

    if (error.message.includes('500') || error.message.includes('Internal Server')) {
      return ERROR_MESSAGES.SERVER_ERROR;
    }

    if (error.message.includes('503') || error.message.includes('Service Unavailable')) {
      return ERROR_MESSAGES.SERVICE_UNAVAILABLE;
    }

    if (error.message.includes('429') || error.message.includes('rate limit') || error.message.includes('Too Many')) {
      return ERROR_MESSAGES.RATE_LIMIT;
    }
  }


  return ERROR_MESSAGES.UNKNOWN_ERROR;
}


export function logError(error: unknown, context?: string): void {
  const timestamp = new Date().toISOString();
  const prefix = context ? `[${context}]` : '[Error]';

  console.error(`${prefix} ${timestamp}:`, error);


  if (import.meta.env.DEV && error instanceof Error && error.stack) {
    console.error('Stack trace:', error.stack);
  }
}


export function handleError(error: unknown, context?: string): string {

  logError(error, context);


  return getUserMessage(error);
}


export function classifyError(error: unknown): ErrorType {
  if (error instanceof ValidationError) {
    return ErrorType.VALIDATION;
  }

  if (error instanceof AppError) {
    return error.type;
  }

  if (error instanceof Error) {
    const message = error.message.toLowerCase();

    if (message.includes('auth') || message.includes('login') || message.includes('password')) {
      return ErrorType.AUTHENTICATION;
    }

    if (message.includes('unauthorized') || message.includes('forbidden') || message.includes('permission')) {
      return ErrorType.AUTHORIZATION;
    }

    if (message.includes('not found') || message.includes('does not exist')) {
      return ErrorType.NOT_FOUND;
    }

    if (message.includes('duplicate') || message.includes('already exists')) {
      return ErrorType.CONFLICT;
    }

    if (message.includes('network') || message.includes('fetch') || message.includes('timeout')) {
      return ErrorType.NETWORK;
    }

    if (message.includes('database') || message.includes('query') || message.includes('relation')) {
      return ErrorType.DATABASE;
    }
  }

  return ErrorType.UNKNOWN;
}


export function createSafeErrorResponse(error: unknown, context?: string) {
  logError(error, context);

  return {
    message: getUserMessage(error),
    type: classifyError(error),
    timestamp: new Date().toISOString(),
  };
}


export function withErrorHandling<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context: string
): T {
  return (async (...args: Parameters<T>) => {
    try {
      return await fn(...args);
    } catch (error) {
      const message = handleError(error, context);
      throw new AppError(message, classifyError(error), message);
    }
  }) as T;
}
